import React, { useEffect, useState } from "react";
import api from "../api";
import { useLocation, useNavigate } from "react-router-dom";
import viewIcon from "../assets/Images/View.png";
import downloadIcon from "../assets/Images/Download.png";
import filterIcon from "../assets/Images/Filter.png";
import usageIcon from "../assets/icons/users.png"; // Add the correct usage icon path
import { formatDateToUserTimeZone } from "../utils/formatDate";
import TopTitleBar from "./TopTitleBar";
// import ViewConversationModal from "./ViewConversationModal";
import { pdf } from "@react-pdf/renderer";
import ChatPDF from "../components/ChatPDF"; // adjust path
import "jspdf-autotable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const CHAT_API_URL = process.env.REACT_APP_API_URL;

const UserSessionManagement = () => {
  const location = useLocation();
  const {
    user_id,
    enterpriseData = {},
    user_name = "N/A",
  } = location.state || {};

  const navigate = useNavigate();

  const [sessionData, setSessionData] = useState([]);
  const [chatStats, setChatStats] = useState({
    total: 0,
    active: 0,
    completed: 0,
    flagged: 0,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(50);
  const [sortOrder, setSortOrder] = useState("desc");
  const [dayFilter, setDayFilter] = useState("");
  const [showModal, setShowModal] = useState(false);

  const [selectedSessionId, setSelectedSessionId] = useState(null);

  const [isDownloadingPDF, setIsDownloadingPDF] = useState(false);
  const [isDownloadingCSV, setIsDownloadingCSV] = useState(false);

  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token");

  const fetchUserSessions = async () => {
    try {
      const res = await api.get(`${CHAT_API_URL}/users/${user_id}/sessions`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      let filtered = res.data;

      // Apply search filter
      if (searchTerm) {
        filtered = filtered.filter((item) =>
          item.session_id
            .toLowerCase()
            .includes(searchTerm.trim().toLowerCase())
        );
      }

      // Apply sort order
      filtered = filtered.sort((a, b) => {
        const dateA = new Date(a.last_updated);
        const dateB = new Date(b.last_updated);
        return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
      });

      // Apply day filter
      if (dayFilter === "Today") {
        const today = new Date().toDateString();
        filtered = filtered.filter(
          (item) => new Date(item.last_updated).toDateString() === today
        );
      } else if (dayFilter === "Yesterday") {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        filtered = filtered.filter(
          (item) =>
            new Date(item.last_updated).toDateString() ===
            yesterday.toDateString()
        );
      }

      setSessionData(filtered);
    } catch (err) {
      console.error("Failed to fetch user sessions:", err);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await api.get(`${CHAT_API_URL}/chat/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setChatStats(res.data);
    } catch (err) {
      console.error("Failed to fetch chat stats:", err);
    }
  };

  useEffect(() => {
    fetchUserSessions();
  }, [searchTerm, sortOrder, dayFilter]);

  useEffect(() => {
    fetchStats();
  }, []);


  const handleDownloadPDF = async () => {
    if (!selectedSessionId) return;
    setIsDownloadingPDF(true); // start loading
    try {
      const res = await api.get(
        `${CHAT_API_URL}/chat/session1/${selectedSessionId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const data = res.data?.data?.sessionMessages || [];

      // Create PDF blob in browser
      const blob = await pdf(
        <ChatPDF sessionId={selectedSessionId} messages={data} />
      ).toBlob();

      // Trigger download
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${selectedSessionId}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error generating PDF:", err);
    } finally {
      setIsDownloadingPDF(false); // end loading
    }
  };

  const handleDownloadCSV = async () => {
    if (!selectedSessionId) return;
    setIsDownloadingCSV(true); // start loading
    try {
      const res = await api.get(
        `${CHAT_API_URL}/chat/session1/${selectedSessionId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const messages = res.data?.data?.sessionMessages || [];
      const data = [];

      // Add header row
      data.push(["Role", "Message", "Time"]);

      // Fill data rows
      messages.forEach((msg) => {
        const time = new Date(msg.createdAt)
          .toISOString()
          .replace("T", " ")
          .split(".")[0]; // Format: YYYY-MM-DD HH:MM:SS
        data.push(["User", msg.user_input, time]);
        data.push(["Bot", msg.bot_response, time]);
      });

      // Create worksheet and workbook
      const worksheet = XLSX.utils.aoa_to_sheet(data);

      // Auto column width based on content
      const columnWidths = data[0].map((_, colIndex) => {
        return {
          wch: Math.max(
            ...data.map((row) =>
              row[colIndex] ? row[colIndex].toString().length : 0
            )
          ),
        };
      });
      worksheet["!cols"] = columnWidths;

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Chat Transcript");

      // Write file and trigger download
      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      const blob = new Blob([excelBuffer], {
        type: "application/octet-stream",
      });
      saveAs(blob, `${selectedSessionId}.xlsx`);
    } catch (err) {
      console.error("Error generating Excel:", err);
    } finally {
      setIsDownloadingCSV(false); // end loading
    }
  };

  const totalEntries = sessionData.length;
  const totalPages = Math.ceil(totalEntries / entriesPerPage);
  const showingFrom =
    totalEntries === 0 ? 0 : (currentPage - 1) * entriesPerPage + 1;
  const showingTo = Math.min(currentPage * entriesPerPage, totalEntries);
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  const handleEntriesChange = (e) => {
    setEntriesPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handlePageClick = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleReset = () => {
    setSortOrder("desc");
    setDayFilter("");
  };

  const handleApply = () => {
    fetchUserSessions();
  };

  const exportExcel = () => {
    console.log("Export to Excel logic here");
  };

  return (
    <div className="mx-2 pt-1 h-100">
      {/* Stats Boxes */}
      <div className="d-flex flex-wrap gap-3 mb-0 align-items-stretch">
        {[
          {
            label: "Total Conversations",
            value: chatStats?.session?.total || 0,
          },
          {
            label: "Active Conversations",
            value: chatStats?.session?.active || 0,
          },
          {
            label: "Completed Conversations",
            value: chatStats?.session?.completed || 0,
          },
        ].map((box, index) => (
          <div className="dashboard-box p-3 rounded shadow-sm d-flex flex-column justify-content-between" key={index}>
            <div className="d-flex align-items-center mb-2">
              <img
                src={usageIcon}
                alt={box.label}
                style={{
                  width: "24px",
                  height: "24px",
                }}
                className="icon-image1"
              />
              <h6 className="ms-2 mb-0">{box.label}</h6>
            </div>
            <h2 className="fw-bold">{box.value}</h2>
          </div>
        ))}
      </div>

      {/* Header */}
      {/* <div className="d-flex justify-content-between align-items-center mb-2 mt-1">
        <span className="fw-bold" style={{ fontSize: "20px" }}>
          User Session Details – <span className="">{user_name}</span>
        </span>
      </div> */}

      <TopTitleBar level="user" user_name={user_name} />

      {/* Filters & Search */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="d-flex align-items-center">
          <select
            className="form-select w-auto"
            value={entriesPerPage}
            onChange={handleEntriesChange}
          >
            {[50, 100, 500].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <label className="ms-2 mt-2">Entries per page</label>
        </div>

        <div className="d-flex align-items-center gap-2 ">
          <div className="custom-search-wrapper ">
            <div className="input-group custom-search-group">
              <span className="input-group-text">
                <i className="bi bi-search"></i>
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Search Conversations"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>

          {/* <button
            className="btn btn-outline-secondary me-2 action-button"
            onClick={exportExcel}
            title="Export to Excel"
          >
            <img src={downloadIcon} className="icon-image" alt="Download" />
          </button> */}

          <div className="">
            <button
              className="icon-button filter-dropdown-toggle action-button m-0"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
              title="Filter"
            >
              <img src={filterIcon} className="icon-image" alt="Filter" />
            </button>

            <div
              className="dropdown-menu profile-menu-dropdown p-2"
              style={{ minWidth: "200px" }}
            >
              <h6 className="fw-bolder refresh-prompts">Time</h6>
              <hr className="mt-0" />
              {["asc", "desc"].map((order) => (
                <div className="form-check" key={order}>
                  <input
                    className="form-check-input"
                    type="radio"
                    name="sortOrder"
                    id={order}
                    value={order}
                    checked={sortOrder === order}
                    onChange={() => setSortOrder(order)}
                  />
                  <label
                    className="form-check-label notification-label"
                    htmlFor={order}
                  >
                    {order === "asc" ? "Ascending" : "Descending"}
                  </label>
                </div>
              ))}

              <hr />
              <h6 className="fw-bolder refresh-prompts">By Day</h6>
              <hr className="mt-0" />
              {["Today", "Yesterday", ""].map((day) => (
                <div className="form-check" key={day || "all"}>
                  <input
                    className="form-check-input"
                    type="radio"
                    name="dayFilter"
                    id={day || "all"}
                    value={day}
                    checked={dayFilter === day}
                    onChange={() => setDayFilter(day)}
                  />
                  <label
                    className="form-check-label fw-5"
                    htmlFor={day || "all"}
                  >
                    {day || "All"}
                  </label>
                </div>
              ))}

              <div className="d-flex justify-content-between mt-3">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={handleReset}
                >
                  Reset
                </button>
                <button
                  className="btn dropdown-btn bg-dark text-white btn-sm"
                  onClick={handleApply}
                >
                  Apply Now
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Table or No Entries */}
      {totalEntries === 0 ? (
        <div className="text-center my-5">
          <p className="refresh-prompts ak-text-md">No entries found.</p>
        </div>
      ) : (
        <div className="not-disable">
          <div className="table-responsive overflow-visible">
            <table className="table conversation-table2 table-sm w-100">
              <thead>
                <tr>
                  <th>Session ID</th>
                  <th className="text-center">Messages</th>
                  <th className="text-center">Models</th>
                  <th>Uploaded On</th>
                  {/* <th>Actions</th> */}
                </tr>
              </thead>
              <tbody>
                {sessionData
                  .slice(
                    (currentPage - 1) * entriesPerPage,
                    currentPage * entriesPerPage
                  )
                  .map((item, index) => (
                    <tr key={index}>
                      <td
                        title={item.session_id}
                        style={{
                          maxWidth: "190px",
                          textOverflow: "ellipsis",
                          overflow: "hidden",
                        }}
                      >
                        {item.session_id}
                      </td>
                      <td className="text-center">{item.total_messages}</td>
                      <td className="text-center">{item.total_models_used}</td>
                      <td>{formatDateToUserTimeZone(item.last_updated)}</td>
                      {/* <td className="align-middle">
                       
                        <button
                          className="icon-button icon-image"
                          title="View"
                          onClick={() =>
                            navigate(`/view-conversation/${item.session_id}`)
                          }
                        >
                          <img
                            src={viewIcon}
                            alt="View"
                            style={{ width: "18px", height: "18px" }}
                          />
                        </button>

                       
                        <button
                          className="icon-button icon-image"
                          data-bs-toggle="modal"
                          data-bs-target="#downloadModal"
                          title="Download"
                          onClick={() => setSelectedSessionId(item.session_id)}
                        >
                          <img src={downloadIcon} alt="Download" />
                        </button>

                      </td> */}
                    </tr>
                  ))}
              </tbody>
            </table>
            <div
              className="modal fade"
              id="downloadModal"
              tabIndex="-1"
              aria-labelledby="downloadModalLabel"
              aria-hidden="true"
            >
              <div
                className="modal-dialog modal-dialog-centered"
                style={{ maxWidth: "700px", margin: "auto" }}
              >
                <div className="modal-content">
                  <div className="modal-header profile-i">
                    <h5 className="modal-title">Download Chat</h5>
                    <button
                      type="button"
                      className="btn-close"
                      data-bs-dismiss="modal"
                      aria-label="Close"
                    ></button>
                  </div>
                  <div className="modal-body text-center profile-i">
                    <div className="d-flex justify-content-center gap-3">
                      <button
                        className="rounded-5 px-3 py-2 download-btn"
                        style={{ backgroundColor: "#6B6B6B40", border: "none" }}
                        onClick={handleDownloadPDF}
                        disabled={isDownloadingPDF}
                      >
                        <span>
                          {isDownloadingPDF ? "Downloading..." : "Download PDF"}
                        </span>
                      </button>

                      <button
                        className="rounded-5 px-3 py-2 download-btn"
                        style={{ backgroundColor: "#6B6B6B40", border: "none" }}
                        onClick={handleDownloadCSV}
                        disabled={isDownloadingCSV}
                      >
                        <span>
                          {isDownloadingCSV ? "Downloading..." : "Download CSV"}
                        </span>
                      </button>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="cancle-btn"
                      data-bs-dismiss="modal"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Pagination */}
          <div className="table-footer bgcolor fixed-bottom-footer d-flex justify-content-between align-items-center">
            <div className="refresh-prompts">
              Showing {showingFrom} to {showingTo} of {totalEntries} entries
            </div>
            <nav>
              <ul className="pagination mb-0">
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(1)}
                  >
                    <i className="bi bi-chevron-double-left"></i>
                  </button>
                </li>
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(currentPage - 1)}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>
                </li>

                {pageNumbers.map((num) => (
                  <li
                    key={num}
                    className={`page-item ${
                      num === currentPage ? "active" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => handlePageClick(num)}
                    >
                      {num}
                    </button>
                  </li>
                ))}

                <li
                  className={`page-item ${
                    currentPage === totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(currentPage + 1)}
                  >
                    <i className="bi bi-chevron-right"></i>
                  </button>
                </li>
                <li
                  className={`page-item ${
                    currentPage === totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(totalPages)}
                  >
                    <i className="bi bi-chevron-double-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserSessionManagement;
